import { splitCamelCase } from "@gettactic/helpers/src/strings/splitCamelCase";
import type { DraggableProvidedDragHandleProps } from "@hello-pangea/dnd";
import { Eye, EyeOff, Lock, Unlock } from "lucide-react";
import React from "react";
import { Shape } from "../../../../types";
import { LayerActions } from "../../../LayerActions";
import { EditorField } from "../../../ui/EditorField";
import { IconButton } from "../../../ui/IconButton";
import { useMapCanvas } from "../../contexts/MapCanvasContext";
import { useMapEditorActions } from "../../contexts/MapEditorContext";
import { LayerPreview } from "./LayerPreview";

export interface LayerItemProps {
  shape: Shape;
  isSelected: boolean;
  isMultiSelected?: boolean;
  depth?: number;
  onClick: (e: React.MouseEvent) => void;
  isFirst?: boolean;
  isLast?: boolean;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
}

const areLayerItemPropsEqual = (
  prev: LayerItemProps,
  next: LayerItemProps
): boolean => {
  if (prev.isSelected !== next.isSelected) return false;
  if (prev.isMultiSelected !== next.isMultiSelected) return false;
  if (prev.isFirst !== next.isFirst) return false;
  if (prev.isLast !== next.isLast) return false;
  if (prev.depth !== next.depth) return false;
  if (prev.dragHandleProps !== next.dragHandleProps) return false;

  const ps = prev.shape;
  const ns = next.shape;

  if (ps === ns) return true;
  if (ps.id !== ns.id) return false;
  if (ps.type !== ns.type) return false;
  if (ps.hidden !== ns.hidden) return false;
  if (ps.locked !== ns.locked) return false;
  if (ps.layerLabel !== ns.layerLabel) return false;
  if (ps.layerType !== ns.layerType) return false;

  // Fields the LayerPreview thumbnail is derived from
  if (ps.color !== ns.color) return false;
  if (ps.noFill !== ns.noFill) return false;
  if (ps.stroke !== ns.stroke) return false;
  if (ps.width !== ns.width) return false;
  if (ps.height !== ns.height) return false;
  if (ps.type === "wall" && ns.type === "wall") {
    if (ps.x2 !== ns.x2 || ps.y2 !== ns.y2 || ps.thickness !== ns.thickness)
      return false;
  }
  if (ps.type === "wall-path" && ns.type === "wall-path") {
    if (ps.vertices !== ns.vertices || ps.closed !== ns.closed) return false;
  }
  if (ps.type === "zone" && ns.type === "zone") {
    if (ps.borderColor !== ns.borderColor) return false;
  }
  if (ps.type === "spot" && ns.type === "spot") {
    if (ps.spotAppearance !== ns.spotAppearance || ps.spotType !== ns.spotType)
      return false;
  }

  if (ps.type === "spot" && ns.type === "spot" && ps.label !== ns.label)
    return false;
  if (ps.type === "text" && ns.type === "text" && ps.text !== ns.text)
    return false;
  if (
    ps.type === "icon" &&
    ns.type === "icon" &&
    (ps.icon.icon !== ns.icon.icon || ps.icon.set !== ns.icon.set)
  )
    return false;
  if (
    ps.type === "furniture" &&
    ns.type === "furniture" &&
    (ps.furniture.name !== ns.furniture.name ||
      ps.furniture.url !== ns.furniture.url)
  )
    return false;
  if (ps.type === "dynamic-furniture" && ns.type === "dynamic-furniture" && ps.layout !== ns.layout) return false;
  if (ps.type === "group" && ns.type === "group") {
    if (ps.children.length !== ns.children.length) return false;
    for (let i = 0; i < ps.children.length; i++) {
      if (ps.children[i].id !== ns.children[i].id) return false;
      if (ps.children[i].hidden !== ns.children[i].hidden) return false;
      if (ps.children[i].locked !== ns.children[i].locked) return false;
      if (ps.children[i].layerLabel !== ns.children[i].layerLabel) return false;
    }
  }

  return true;
};

const LayerItemComponent: React.FC<LayerItemProps> = ({
  shape,
  isSelected,
  isMultiSelected = false,
  depth = 0,
  onClick,
  isFirst = false,
  isLast = false,
  dragHandleProps
}) => {
  const { updateEditingZoneSize } = useMapCanvas();
  const {
    updateShape: onShapeUpdate,
    deleteShape,
    moveShape,
    locateShape,
    ungroupShapes,
    groupSelected
  } = useMapEditorActions();

  const onMoveUp = () => moveShape(shape.id, "up");
  const onMoveDown = () => moveShape(shape.id, "down");
  const onDelete = () => deleteShape(shape.id);
  const onLocate = () => locateShape(shape.id);
  const onUngroup = () => ungroupShapes(shape.id);
  const onGroup = groupSelected;

  // Helper: returns the default name for the shape.
  const getName = () => {
    switch (shape.type) {
      case "text":
        return shape.text.slice(0, 20) + (shape.text.length > 20 ? "..." : "");
      case "rectangle":
        return "Rectangle";
      case "group":
        return "Group";
      case "spot":
        return shape.label;
      case "svg":
        return "SVG Shape";
      case "circle":
        return "Circle";
      case "icon":
        if (shape.icon.set === "mdi") {
          return splitCamelCase(shape.icon.icon.replace(/^mdi/, ""));
        }
        if (shape.icon.set === "lucide") {
          return splitCamelCase(shape.icon.icon);
        }
        return shape.icon.icon;
      case "dynamic-furniture": return "Dynamic table set";
      case "furniture":
        return shape.furniture.name;
      default:
        return "Shape";
    }
  };

  // Local state for inline editing.
  const [isEditing, setIsEditing] = React.useState(false);
  const initialLabel = shape.layerLabel || getName();
  const [editedName, setEditedName] = React.useState(initialLabel);

  // Update local state when the shape changes (if not currently editing).
  React.useEffect(() => {
    if (!isEditing) {
      setEditedName(shape.layerLabel || getName());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shape.layerLabel, shape]);

  // Commits the edit by updating the shape (if changed).
  const commitEdit = () => {
    const trimmedName = editedName.trim();
    if (trimmedName && trimmedName !== (shape.layerLabel || getName())) {
      onShapeUpdate(shape.id, { layerLabel: trimmedName });
    }
    setIsEditing(false);
  };

  // Cancel the editing and revert to the original label.
  const handleCancel = () => {
    setEditedName(shape.layerLabel || getName());
    setIsEditing(false);
  };

  // Allow commit on Enter and cancel on Escape.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      commitEdit();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  // Start editing on double-click.
  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
  };

  const handleUpdateEditingArea = () => {
    updateEditingZoneSize(shape.width, shape.height);
  };

  // Use the provided layerLabel if available or default to getName()
  const layerName = shape.layerLabel || getName();

  // When the user picks a shape on the canvas, the panel should reveal the
  // matching row so move-up/move-down is one click away. `block: 'nearest'`
  // only scrolls when the row is actually outside the visible area, so
  // re-clicking an already-visible selection doesn't jitter the panel.
  const rowRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!isSelected) return;
    rowRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [isSelected]);

  // The action buttons carry Radix tooltip/menu roots — mounting them for
  // every row locks up big maps (1000 rows × 3 Radix roots at open). They
  // only exist while the row is hovered or selected; the hidden/locked
  // indicators additionally stay mounted so disabled layers remain
  // recognizable at a glance.
  const [isRowHovered, setIsRowHovered] = React.useState(false);
  const showActions = isRowHovered || isSelected;

  return (
    <div>
      <div
        ref={rowRef}
        {...dragHandleProps}
        className={`group flex h-8 cursor-pointer select-none items-center gap-2 pr-1.5 pl-3 transition-colors ${
          isSelected ? "bg-blue-50" : "hover:bg-neutral-50"
        }`}
        style={{ paddingLeft: `${depth * 14 + 10}px` }}
        onClick={onClick}
        onMouseEnter={() => setIsRowHovered(true)}
        onMouseLeave={() => setIsRowHovered(false)}
      >
        <div className="flex w-6 flex-none items-center justify-center">
          <LayerPreview shape={shape} />
        </div>

        {isEditing ? (
          <div className="flex flex-grow items-center">
            <EditorField
              label="Layer name"
              type="text"
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              onKeyDown={handleKeyDown}
              containerClassName="flex-grow"
            />
          </div>
        ) : (
          <span
            title={layerName}
            className={`flex-grow truncate text-xs ${
              isSelected ? "font-medium text-blue-700" : "text-neutral-800"
            }`}
            onDoubleClick={handleDoubleClick}
          >
            {layerName}
          </span>
        )}

        {/* Row actions reveal on hover; hidden/locked state stays visible
            so a disabled layer is recognizable at a glance. */}
        <div
          // Fixed width so the truncated name doesn't reflow when the
          // hover-mounted buttons appear.
          className="flex min-w-[76px] flex-none items-center justify-end gap-0.5"
          onClick={(e) => e.stopPropagation()}
        >
          {(showActions || shape.hidden) && (
            <IconButton
              size="sm"
              label={shape.hidden ? "Show" : "Hide"}
              className={shape.hidden ? "text-neutral-500" : undefined}
              onClick={() => onShapeUpdate(shape.id, { hidden: !shape.hidden })}
            >
              {shape.hidden ? <EyeOff size={14} /> : <Eye size={14} />}
            </IconButton>
          )}
          {(showActions || shape.locked) && (
            <IconButton
              size="sm"
              label={shape.locked ? "Unlock" : "Lock"}
              className={shape.locked ? "text-neutral-500" : undefined}
              onClick={() => onShapeUpdate(shape.id, { locked: !shape.locked })}
            >
              {shape.locked ? <Lock size={14} /> : <Unlock size={14} />}
            </IconButton>
          )}
          {showActions && (
            <LayerActions
              onDelete={onDelete}
              onLocate={onLocate}
              onMoveUp={!isLast ? onMoveUp : undefined}
              onMoveDown={!isFirst ? onMoveDown : undefined}
              onUngroup={shape.type === "group" ? onUngroup : undefined}
              onGroup={isMultiSelected ? onGroup : undefined}
              onUpdateEditingArea={
                shape.layerType === "background"
                  ? handleUpdateEditingArea
                  : undefined
              }
            />
          )}
        </div>
      </div>
      {shape.type === "group" && (
        <div className={"ml-6 border-2 border-l"}>
          {shape.children.map((child, childIndex) => (
            <LayerItem
              shape={child}
              key={child.id}
              isSelected={false}
              isFirst={childIndex === 0}
              isLast={shape.children.length === childIndex - 1}
              onClick={() => null}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const LayerItem = React.memo(LayerItemComponent, areLayerItemPropsEqual);
